<template>
  <v-card>
    <div
      id="blurHelper"
      tabindex="-1"
      style="position: absolute; left: -9999px; top: -9999px"
    ></div>
    <v-container>
      <v-row>
        <v-col cols="9" class="pb-0">
          <v-card-title id="title" tabindex="-1" class="text-button">
            {{
              isMobile
                ? "Nueva prescripción"
                : "Nueva prescripción de estudios complementarios"
            }}
          </v-card-title>
        </v-col>
        <v-col cols="3" class="pt-0 pr-0">
          <v-card-actions class="pb-0">
            <v-spacer></v-spacer>
            <v-tooltip text="Cerrar" location="left">
              <template v-slot:activator="{ props }">
                <v-btn
                  color="primary"
                  v-bind="props"
                  :icon="closeIcon"
                  id="vBtnCloseCreatePrescripcionEstudios"
                  tabindex="100"
                  @click="closeModal(false)"
                  @keydown.tab="focusFirstFieldOfCurrentStep($event)"
                ></v-btn>
              </template>
            </v-tooltip>
          </v-card-actions>
        </v-col>
      </v-row>
      <v-card-text class="py-0">
        <v-stepper v-model="step" :items="steps" hide-actions>
          <template v-slot:[`item.1`]>
            <v-card flat>
              <v-form v-model="isPacienteFormValid" ref="paciente-form">
                <v-container :class="isMobile ? 'px-0 mx-0' : 'my-1'">
                  <!-- Step 1 reutiliza el mismo formulario de datos del paciente -->
                  <v-row :no-gutters="isMobile">
                    <v-col :cols="isMobile ? 12 : 4" class="py-0">
                      <v-select
                        :items="financiadoresItems"
                        label="Financiador"
                        v-model="financiadorSelected"
                        autocomplete="off"
                        :tabindex="step === 1 ? 1 : -1"
                        return-object
                        @update:model-value="setDataFinanciador()"
                        variant="outlined"
                        density="compact"
                        item-value="id"
                        item-title="value1"
                        id="FinanciadorSelect"
                        :rules="rules.required"
                      ></v-select>
                    </v-col>

                    <!-- Alerta de bloqueo de prescripción -->
                    <v-col
                      cols="12"
                      class="py-2"
                      v-if="
                        prestadorValidation && prestadorValidation.isBlocked
                      "
                    >
                      <v-alert
                        type="error"
                        variant="tonal"
                        prominent
                        border="start"
                        title="Prescripción bloqueada"
                        :text="
                          prestadorValidation.blockReason ||
                          'No se puede continuar con la prescripción debido a problemas de validación.'
                        "
                        class="mb-4"
                      >
                        <template v-slot:append>
                          <v-icon>mdi-lock</v-icon>
                        </template>
                      </v-alert>
                      <v-alert
                        v-if="financiadoresItems.length > 1"
                        type="info"
                        variant="tonal"
                        density="compact"
                        text="Seleccione otro financiador para reintentar la validación."
                      ></v-alert>
                    </v-col>

                    <!-- Resto del formulario - solo visible si no está bloqueado -->
                    <template
                      v-if="
                        !prestadorValidation || !prestadorValidation.isBlocked
                      "
                    >
                      <v-col :cols="isMobile ? 12 : 3" class="py-0">
                        <DatePicker
                          @selectFecha="selectFechaEmision($event)"
                          label="Fecha prescripción"
                          :minDate="minDateFechaEmisionFormatted"
                          :maxDate="maxDateFechaEmisionFormatted"
                          :disabled="false"
                          :tabindex="step === 1 ? 2 : -1"
                          :dateMenu="menuFechaEmision"
                          :defaultDate="fechaEmision"
                          :rules="[
                            ...rules.required,
                            ...rules.validDateFormat,
                            financiadorSelected != null
                              ? rules.dateSelectedInRange(
                                  fechaEmision,
                                  minDateFechaEmision,
                                  maxDateFechaEmision
                                )
                              : true,
                            rules.validDateRange(
                              selectedFechaNacimiento,
                              selectedFechaEmision
                            ),
                            rules.validDateRange(
                              formatDate(new Date()),
                              selectedFechaEmision
                            ),
                          ]"
                        />
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 5"
                        class="py-0"
                        v-if="
                          validaAfiliado != null &&
                          validaAfiliado &&
                          financiadorSelected != null
                        "
                      >
                        <v-autocomplete
                          label="Afiliado"
                          v-model="selectedAfiliado"
                          :items="afiliados"
                          autocomplete="off"
                          v-model:search="inputAfiliado"
                          ref="afiliados-autocomplete"
                          id="AfiliadoAutocomplete"
                          :tabindex="step === 1 ? 3 : -1"
                          return-object
                          class="mr-2"
                          variant="outlined"
                          density="compact"
                          item-title="docId"
                          item-value="nroAfiliado"
                          :hint="
                            financiadorSelected != null &&
                            fechaEmision != null &&
                            selectedAfiliado == null
                              ? 'Búsqueda por CUIL, DNI o N° afiliado'
                              : selectedAfiliado != null
                              ? ''
                              : 'Seleccionar fecha y financiador para buscar afiliado'
                          "
                          persistent-hint
                          :loading="loadingAfiliados"
                          :custom-filter="customFilterAfiliado"
                          :readonly="
                            selectedFechaEmision == null ||
                            financiadorSelected == null
                          "
                          clearable
                          :rules="validaAfiliado ? rules.required : []"
                          :menu-props="{ closeOnContentClick: true }"
                        >
                          <template v-slot:item="{ item }">
                            <v-list-item @click="setAfiliado(item)">
                              <v-list-item-title>{{
                                item.raw.benNombreCompleto
                              }}</v-list-item-title>
                              <v-list-item-subtitle
                                >N° afiliado:
                                {{ item.value }}</v-list-item-subtitle
                              >
                              <v-list-item-subtitle
                                >CUIL: {{ item.raw.cuil }}</v-list-item-subtitle
                              >
                              <v-list-item-subtitle
                                >DNI: {{ item.raw.docId }}</v-list-item-subtitle
                              >
                              <v-list-item-subtitle
                                v-if="item.raw.estadoBenef != null"
                                >Estado:
                                {{
                                  item.raw.estadoBenef.value
                                }}</v-list-item-subtitle
                              >
                            </v-list-item>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <!-- El resto de Step 1 se mantiene igual que en CreateRecetaElectronica -->
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        :class="isMobile ? 'pt-1' : ''"
                        v-if="
                          validaAfiliado != null &&
                          validaAfiliado &&
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          :items="planesItems"
                          label="Plan"
                          v-model="planSelected"
                          return-object
                          variant="outlined"
                          density="compact"
                          item-value="id"
                          :readonly="validaAfiliado"
                          item-title="value"
                          :tabindex="step === 1 ? 4 : -1"
                          :rules="rules.required"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 10 : 3"
                        class="py-0"
                        :class="isMobile ? 'pt-1' : ''"
                        v-else-if="
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          financiadorSelected != null &&
                          !afiliadoSearched &&
                          solicitudItem == null
                        "
                      >
                        <v-text-field
                          label="Buscar afiliado"
                          variant="outlined"
                          density="compact"
                          hint="Búsqueda por CUIL, DNI o N° afiliado"
                          persistent-hint
                          :tabindex="step === 1 ? 3 : -1"
                          :rules="rules.required"
                          v-model="searchAfiliadoField"
                          @keydown.enter="searchAfiliado()"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 2 : 1"
                        class="py-0"
                        :class="isMobile ? 'pt-2 pl-1 pr-0' : ''"
                        v-if="
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          financiadorSelected != null &&
                          !afiliadoSearched &&
                          solicitudItem == null
                        "
                      >
                        <v-tooltip text="Buscar afiliado" location="left">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              color="primary"
                              :icon="searchIcon"
                              id="buscarAfiliadoBtn"
                              :tabindex="step === 1 ? 4 : -1"
                              v-bind="props"
                              :loading="isLoadingSearchAfiliado"
                              :disabled="searchAfiliadoField == null"
                              @click="searchAfiliado()"
                              :size="isMobile ? 'x-small' : 'small'"
                            ></v-btn>
                          </template>
                        </v-tooltip>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        :class="isMobile ? 'pt-1' : ''"
                        v-if="
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          financiadorSelected != null &&
                          afiliadoSearched
                        "
                      >
                        <v-text-field
                          label="Plan"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 5 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(planSelected, 50)],
                              rules.nameRulesWithNumber
                            )
                          "
                          v-model="planSelected"
                        ></v-text-field>
                      </v-col>

                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          selectedAfiliado != null &&
                          validaAfiliado != null &&
                          validaAfiliado
                        "
                      >
                        <v-text-field
                          label="Apellido/s y nombre/s paciente"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 5 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteApellidoNombre, 50)],
                              rules.nameRules
                            )
                          "
                          :model-value="pacienteApellidoNombre"
                          readonly
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : !validaAfiliado ? 4 : 3"
                        class="py-0"
                        v-if="
                          financiadorSelected != null &&
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          afiliadoSearched
                        "
                      >
                        <v-text-field
                          label="Apellido/s"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 6 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteApellido, 50)],
                              rules.nameRules
                            )
                          "
                          v-model="pacienteApellido"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          financiadorSelected != null &&
                          validaAfiliado != null &&
                          !validaAfiliado &&
                          afiliadoSearched
                        "
                      >
                        <v-text-field
                          label="Nombre/s"
                          variant="outlined"
                          density="compact"
                          :tabindex="step === 1 ? 7 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteNombre, 50)],
                              rules.nameRules
                            )
                          "
                          v-model="pacienteNombre"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 8 : 2"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          :items="tiposDocItems"
                          label="Tipo documento"
                          v-model="tipoDocSelected"
                          :tabindex="step === 1 ? 8 : -1"
                          return-object
                          variant="outlined"
                          density="compact"
                          item-value="id"
                          item-title="value1"
                          :rules="rules.required"
                          @update:model-value="handlePacienteDataChange"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          v-if="shouldMaskDocumento"
                          label="Número documento"
                          density="compact"
                          variant="outlined"
                          id="textFieldDocumento"
                          :tabindex="step === 1 ? 9 : -1"
                          v-mask="['#.###.###', '##.###.###']"
                          :rules="rules.required"
                          v-model="pacienteDocumento"
                          @update:model-value="handlePacienteDataChange"
                        ></v-text-field>
                        <v-text-field
                          v-else
                          label="Número documento"
                          density="compact"
                          variant="outlined"
                          id="textFieldDocumento"
                          :tabindex="step === 1 ? 9 : -1"
                          :rules="
                            rules.required.concat([
                              rules.maxLength(pacienteDocumento, 20),
                            ])
                          "
                          v-model="pacienteDocumento"
                          @update:model-value="handlePacienteDataChange"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="CUIL"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 10 : -1"
                          v-mask="['##-#######-#', '##-########-#']"
                          :rules="
                            isDocumentoDNI
                              ? rules.required
                              : pacienteCuil
                              ? [rules.validateCUITCUIL(pacienteCuil)]
                              : []
                          "
                          v-model="pacienteCuil"
                        >
                          <template v-slot:append-inner v-if="showGenerateCuil">
                            <v-btn
                              color="primary"
                              variant="tonal"
                              rounded="pill"
                              density="comfortable"
                              size="small"
                              class="text-none"
                              :loading="generatingCuil"
                              @click.stop="generarCUIL"
                            >
                              Generar CUIL
                            </v-btn>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          :items="generosItems"
                          label="Sexo"
                          v-model="generoSelected"
                          return-object
                          :tabindex="step === 1 ? 11 : -1"
                          variant="outlined"
                          density="compact"
                          item-value="id"
                          item-title="value"
                          :rules="rules.required"
                          @update:model-value="handlePacienteDataChange"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 2"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="Número afiliado"
                          density="compact"
                          variant="outlined"
                          id="textFieldNroAfiliado"
                          :tabindex="step === 1 ? 12 : -1"
                          :readonly="validaAfiliado"
                          v-mask="'##############'"
                          v-model="pacienteNumero"
                          :rules="rules.required"
                          @update:model-value="handlePacienteDataChange"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <DatePicker
                          @selectFecha="selectFechaNacimiento($event)"
                          label="Fecha nacimiento"
                          :minDate="minDateFechaNacimiento"
                          :maxDate="maxDateFechaNacimientoFormatted"
                          :disabled="false"
                          :tabindex="step === 1 ? 13 : -1"
                          :dateMenu="menuFechaNacimiento"
                          :defaultDate="fechaNacimiento"
                          :rules="[
                            ...rules.required,
                            ...rules.validDateFormat,
                            rules.validDateRange(
                              selectedFechaNacimiento,
                              formatDate(new Date())
                            ),
                          ]"
                        />
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="Email"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 14 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteEmail, 100)],
                              rules.required,
                              rules.emailRules
                            )
                          "
                          v-model="pacienteEmail"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 3"
                        class="py-0 pr-0 mr-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-select
                          v-model="selectedPrefix"
                          :items="countryPrefixes"
                          label="Prefijo"
                          item-value="value"
                          item-title="text"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 15 : -1"
                          class="mr-0 pr-0"
                        ></v-select>
                      </v-col>
                      <v-col
                        :cols="isMobile ? 12 : 4"
                        class="py-0"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-text-field
                          label="Teléfono"
                          density="compact"
                          variant="outlined"
                          :tabindex="step === 1 ? 16 : -1"
                          :rules="
                            rules.required.concat(
                              [rules.maxLength(pacienteTelefono, 50)],
                              rules.required,
                              rules.telephoneRules
                            )
                          "
                          :prefix="selectedPrefix"
                          v-model="pacienteTelefono"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        class="py-0"
                        :cols="isMobile ? 12 : 12"
                        v-if="
                          (financiadorSelected != null &&
                            validaAfiliado != null &&
                            !validaAfiliado &&
                            afiliadoSearched) ||
                          selectedAfiliado != null
                        "
                      >
                        <v-alert
                          type="info"
                          variant="tonal"
                          density="compact"
                          class="to-right"
                        >
                          Importante: verifique que los datos ingresados sean
                          correctos.
                        </v-alert>
                      </v-col>
                    </template>
                  </v-row>
                </v-container>
              </v-form>
            </v-card>
          </template>

          <template v-slot:[`item.2`]>
            <v-card flat>
              <v-form v-model="isPrescripcionFormValid" ref="prescripcion-form">
                <v-container :class="isMobile ? 'px-0' : ''">
                  <v-row>
                    <v-col :cols="isMobile ? 12 : 12" class="py-1">
                      <v-textarea
                        v-model="diagnostico"
                        label="Diagnóstico"
                        variant="outlined"
                        density="compact"
                        auto-grow
                        rows="1"
                        id="diagnosticoField"
                        :tabindex="step === 2 ? 1 : -1"
                        :rules="
                          rules.required.concat([
                            rules.maxLength(diagnostico, 500),
                          ])
                        "
                      ></v-textarea>
                    </v-col>

                    <!-- Selección de matrícula -->
                    <v-col
                      :cols="isMobile ? 12 : 4"
                      class="py-1"
                      v-if="matriculasDisponibles.length > 1"
                    >
                      <v-select
                        :items="matriculasDisponibles"
                        label="Matrícula a utilizar"
                        v-model="matriculaSeleccionada"
                        return-object
                        autocomplete="off"
                        variant="outlined"
                        density="compact"
                        item-title="display"
                        item-value="preXfinId"
                        :tabindex="step === 2 ? 3 : -1"
                        :rules="rules.required"
                        @update:model-value="onMatriculaChange"
                      ></v-select>
                    </v-col>
                    <v-col
                      :cols="isMobile ? 12 : 4"
                      class="py-1"
                      v-else-if="matriculasDisponibles.length === 1"
                    >
                      <v-text-field
                        label="Matrícula"
                        variant="outlined"
                        density="compact"
                        readonly
                        :model-value="matriculasDisponibles[0]?.display"
                        tabindex="-1"
                      ></v-text-field>
                    </v-col>
                    <v-col :cols="isMobile ? 12 : 8" class="py-1">
                      <v-textarea
                        v-model="domicilioPrescripcion"
                        label="Domicilio de prescripción"
                        variant="outlined"
                        density="compact"
                        auto-grow
                        rows="1"
                        id="domicilioField"
                        :tabindex="step === 2 ? 2 : -1"
                        :rules="
                          rules.required.concat([
                            rules.maxLength(domicilioPrescripcion, 200),
                          ])
                        "
                      ></v-textarea>
                    </v-col>
                    <v-divider v-if="!isMobile"></v-divider>
                    <!-- Agregar nuevo estudio -->
                    <v-col cols="12" class="pt-4">
                      <v-row :no-gutters="isMobile" class="align-center">
                        <v-col :cols="isMobile ? 12 : 8" class="pb-0">
                          <v-textarea
                            label="Descripción del estudio"
                            variant="outlined"
                            density="compact"
                            auto-grow
                            rows="1"
                            class="mr-2"
                            id="descripcionEstudio"
                            :tabindex="step === 2 ? 4 : -1"
                            v-model="estudioActual.descripcion"
                            :rules="
                              [
                                rules.maxLength(estudioActual.descripcion, 500),
                              ].concat(rules.noStartingSpaces)
                            "
                            placeholder="Describa el estudio a prescribir"
                          ></v-textarea>
                        </v-col>
                        <!--
                        <v-col :cols="isMobile ? 12 : 3" class="pb-0">
                          <v-text-field
                            label="Código práctica"
                            variant="outlined"
                            density="compact"
                            class="mr-2"
                            id="codigoEstudio"
                            :tabindex="step === 2 ? 4 : -1"
                            v-model="estudioActual.codigo"
                            :rules="[rules.maxLength(estudioActual.codigo, 20)]"
                          ></v-text-field>
                        </v-col>
                        -->
                        <v-col :cols="isMobile ? 6 : 2" class="pb-0">
                          <v-text-field
                            label="Cantidad"
                            variant="outlined"
                            density="compact"
                            min="1"
                            max="99"
                            class="center-input mr-2"
                            id="cantidadEstudio"
                            type="number"
                            :tabindex="step === 2 ? 5 : -1"
                            v-model="estudioActual.cantidad"
                            :rules="
                              rules.required.concat(rules.validNumberRule99)
                            "
                          ></v-text-field>
                        </v-col>
                        <v-col :cols="isMobile ? 6 : 2" class="pt-0">
                          <v-btn
                            density="compact"
                            color="primary"
                            :tabindex="step === 2 ? 6 : -1"
                            @click="addEstudio()"
                            :disabled="!canAddEstudio"
                          >
                            {{
                              editingIndex !== null ? "Actualizar" : "Agregar"
                            }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Tabla de estudios agregados -->
                    <v-col cols="12" :class="isMobile ? 'pb-0 pt-3' : 'py-0'">
                      <v-data-table
                        :items="estudiosAgregados"
                        item-value="id"
                        :mobile="isMobile"
                        :hide-default-header="isMobile"
                        :hide-no-data="isMobile"
                        density="compact"
                        :headers="headersEstudiosAgregados"
                        :hide-default-footer="estudiosAgregados.length <= 10"
                      >
                        <template v-slot:[`item.descripcion`]="{ item }">
                          <span :style="isMobile ? 'font-size: 10px' : ''">
                            {{
                              (item.descripcion ?? "").length > 80
                                ? item.descripcion.substring(0, 77) + "..."
                                : item.descripcion ?? ""
                            }}
                          </span>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                          <v-tooltip text="Editar estudio" location="start">
                            <template v-slot:activator="{ props }">
                              <v-icon
                                size="20"
                                @click="editEstudio(item)"
                                @keydown.enter="editEstudio(item)"
                                v-bind="props"
                              >
                                {{ editIcon }}
                              </v-icon>
                            </template>
                          </v-tooltip>
                          <v-tooltip text="Eliminar estudio" location="start">
                            <template v-slot:activator="{ props }">
                              <v-icon
                                size="20"
                                v-bind="props"
                                @click="deleteEstudio(item)"
                                @keydown.enter="deleteEstudio(item)"
                              >
                                {{ deleteIcon }}
                              </v-icon>
                            </template>
                          </v-tooltip>
                        </template>
                      </v-data-table>

                      <!-- Información sobre límites -->
                      <v-alert
                        v-if="estudiosAgregados.length >= 30"
                        :type="
                          estudiosAgregados.length >= 40 ? 'warning' : 'info'
                        "
                        variant="tonal"
                        density="compact"
                        class="mt-2"
                      >
                        <template v-if="estudiosAgregados.length >= 40">
                          Se ha alcanzado el límite máximo de 40 estudios
                          complementarios.
                        </template>
                        <template v-else>
                          {{ estudiosAgregados.length }} / 40 estudios
                          agregados.
                        </template>
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-card>
          </template>

          <template v-slot:[`item.3`]>
            <v-card flat>
              <v-container :class="isMobile ? 'px-0' : ''">
                <v-row>
                  <v-col :cols="isMobile ? 12 : 3" class="pb-0">
                    <v-text-field
                      tabindex="-1"
                      label="Fecha prescripción"
                      variant="outlined"
                      density="compact"
                      style="color: #000"
                      v-model="selectedFechaEmision"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 5" class="pb-0">
                    <v-text-field
                      tabindex="-1"
                      label="Apellido/s y nombre/s paciente"
                      variant="outlined"
                      density="compact"
                      :model-value="pacienteApellidoNombre"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 4" class="pb-0">
                    <v-text-field
                      tabindex="-1"
                      label="Email"
                      variant="outlined"
                      density="compact"
                      v-model="pacienteEmail"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 2" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="Tipo documento"
                      variant="outlined"
                      density="compact"
                      v-model="tipoDocSelected.value1"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 3" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="N° documento"
                      variant="outlined"
                      density="compact"
                      v-model="pacienteDocumento"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 3" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="CUIL"
                      variant="outlined"
                      density="compact"
                      v-model="pacienteCuil"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 2" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="Sexo"
                      variant="outlined"
                      density="compact"
                      v-model="generoSelected.value"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 2" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="Fecha nacimiento"
                      variant="outlined"
                      density="compact"
                      v-model="selectedFechaNacimiento"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 4" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="Financiador"
                      variant="outlined"
                      density="compact"
                      v-model="financiadorSelected.value1"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col
                    :cols="isMobile ? 12 : 4"
                    class="py-0"
                    v-if="validaAfiliado"
                  >
                    <v-text-field
                      tabindex="-1"
                      label="Plan"
                      variant="outlined"
                      density="compact"
                      v-model="planSelected.value"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 4" class="py-0" v-else>
                    <v-text-field
                      tabindex="-1"
                      label="Plan"
                      variant="outlined"
                      density="compact"
                      v-model="planSelected"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 2" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="N° afiliado"
                      variant="outlined"
                      density="compact"
                      v-model="pacienteNumero"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col :cols="isMobile ? 12 : 2" class="py-0">
                    <v-text-field
                      tabindex="-1"
                      label="Teléfono"
                      variant="outlined"
                      density="compact"
                      v-model="pacienteTelefono"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" class="py-0">
                    <v-textarea
                      tabindex="-1"
                      v-model="diagnostico"
                      label="Diagnóstico"
                      variant="outlined"
                      density="compact"
                      auto-grow
                      rows="1"
                      readonly
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12" class="py-0">
                    <v-textarea
                      tabindex="-1"
                      v-model="domicilioPrescripcion"
                      label="Domicilio de prescripción"
                      variant="outlined"
                      density="compact"
                      auto-grow
                      rows="1"
                      readonly
                    ></v-textarea>
                  </v-col>
                  <!-- Estudios prescritos en vista previa -->
                  <v-col cols="12" class="py-0">
                    <v-data-table
                      :mobile="isMobile"
                      :hide-default-header="isMobile"
                      :hide-no-data="isMobile"
                      :items="estudiosAgregados"
                      item-value="id"
                      density="compact"
                      :headers="vistaPreviaHeadersEstudiosAgregados"
                      :hide-default-footer="estudiosAgregados.length <= 10"
                    >
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </template>
        </v-stepper>
      </v-card-text>
      <v-card-actions>
        <v-row>
          <v-col cols="6" class="py-0">
            <v-btn
              @click="prevStep"
              color="red"
              id="atrasBtn"
              tabindex="98"
              :disabled="step == 1"
              >{{ previousText }}</v-btn
            >
          </v-col>
          <v-col cols="6" class="py-0">
            <v-btn
              class="to-right"
              @click="nextStep"
              id="siguienteBtn"
              color="primary"
              tabindex="99"
              :loading="loadingCreateRecipeBtn"
              :disabled="isNextStepDisabled"
              >{{ nextText }}</v-btn
            >
          </v-col>
        </v-row>
      </v-card-actions>
      <v-dialog
        v-if="dialogSendRecipe"
        v-model="dialogSendRecipe"
        :max-width="isMobile ? '95%' : '35%'"
        persistent
      >
        <SendRecipe
          :link="recipeLink"
          :email="pacienteEmail"
          :telefono="selectedPrefix + pacienteTelefono"
          :paciente="pacienteApellidoNombre"
          :solicitudId="solicitudId"
          tipoPrescripcion="estudios"
          @closeModal="closeModal($event)"
        />
      </v-dialog>
    </v-container>
  </v-card>
</template>

<script>
import enums from "@/utils/enums/index";
import DatePicker from "@/components/shared/DatePicker.vue";
import helperDate from "@/utils/helpers/dateTimePicker";
import { mapActions, mapGetters } from "vuex";
import rules from "@/utils/helpers/rules";
import { focusComponent } from "@/utils/helpers/htmlHelpers";
import SendRecipe from "@/components/modules/solicitudes/SendRecipe.vue";
import cuilHelper from "@/utils/helpers/cuilHelper";

export default {
  name: "CreatePrescripcionEstudios",
  components: { DatePicker, SendRecipe },
  props: {
    solicitudItem: { type: Object, required: false },
  },
  data() {
    return {
      step: 1,
      rules: rules,
      steps: ["Datos del paciente", "Prescripción", "Vista previa"],
      completedSteps: [],
      closeIcon: enums.icons.CLOSE_ICON,
      addIcon: enums.icons.ADD_ICON,
      deleteIcon: enums.icons.DELETE_ICON,
      editIcon: enums.icons.EDIT_ICON,
      pacienteApellido: null,
      pacienteNombre: null,
      tiposDocItems: [],
      tipoDocSelected: null,
      pacienteDocumento: null,
      pacienteCuil: null,
      generatingCuil: false,
      generosItems: [],
      generoSelected: null,
      menuFechaEmision: false,
      fechaEmision: null,
      selectedFechaEmision: null,
      menuFechaNacimiento: false,
      fechaNacimiento: null,
      selectedFechaNacimiento: null,
      minDateFechaNacimiento: "1910",
      minDateFechaEmision: new Date().setHours(0, 0, 0, 0),
      minDateFechaEmisionFormatted: helperDate.convertDateFormat(
        helperDate.formatDate(new Date())
      ),
      maxDateFechaEmisionFormatted: null,
      maxDateFechaEmision: null,
      maxDateFechaNacimiento: null,
      maxDateFechaNacimientoFormatted: null,
      pacienteNumero: null,
      financiadoresItems: [],
      financiadorSelected: null,
      planesItems: [],
      planSelected: null,
      pacienteEmail: null,
      pacienteTelefono: null,
      previousText: "Volver",
      nextText: "Siguiente",
      prestadorApellidoNombre: null,
      provinciasItems: [],
      provinciaSelected: null,
      prestadorMatricula: null,
      matriculasDisponibles: [],
      matriculaSeleccionada: null,
      prestadorValidation: null,
      prestadorDataActual: null,
      selectedPrestador: null,
      prestadores: [],
      loadingPrestadores: false,
      selectedAfiliado: null,
      inputAfiliado: "",
      afiliados: [],
      loadingAfiliados: false,
      diagnostico: null,
      domicilioPrescripcion: null,
      isPacienteFormValid: false,
      isPrescripcionFormValid: true,
      datosContacto: "",
      emailContacto: "",
      nombreConsultorio: "",
      direccionConsultorio: "",
      leyenda: "",
      imprimirDiagnostico: "",
      loadingCreateRecipeBtn: false,
      selectedPrefix: "+54",
      countryPrefixes: enums.enums.COUNTRYS_PREFIXES,
      afiliadoWithUploadedData: false,
      searchIcon: enums.icons.SEARCH_ICON,
      validaAfiliado: null,
      validaCobertura: false,
      searchAfiliadoField: null,
      afiliadoSearched: false,
      isLoadingSearchAfiliado: false,
      timerAfiliado: null,
      alertEstudiosDisplayed: false,
      // Estudios complementarios específicos
      estudioActual: {
        codigo: null,
        descripcion: null,
        cantidad: 1,
      },
      estudiosAgregados: [],
      editingIndex: null,
      headersEstudiosAgregados: [
        { title: "Descripción", sortable: false, key: "descripcion" },
        { title: "Cantidad", align: "end", sortable: false, key: "cantidad" },
        { title: "Acciones", align: "end", sortable: false, key: "actions" },
      ],
      vistaPreviaHeadersEstudiosAgregados: [
        { title: "Descripción", sortable: false, key: "descripcion" },
        { title: "Cantidad", sortable: false, key: "cantidad", align: "end" },
      ],
      prestadorData: null,
      dialogSendRecipe: false,
      recipeLink: null,
      solicitudId: null,
    };
  },
  async created() {
    this.setDefaultDates();
    await this.setSelects();
    if (this.solicitudItem != null) this.setRecetaToCopy();
  },
  watch: {
    selectedFechaEmision(val) {
      if (
        this.financiadorSelected !== null &&
        this.selectedAfiliado !== null &&
        val !== null &&
        val !== undefined
      ) {
        // Revalidar afiliado/cobertura al cambiar fecha
        this.findAfiliado();
      }
    },
    inputAfiliado(val) {
      if ((val?.trim() && val?.trim().length >= 3) || this.selectedAfiliado) {
        this.findAfiliado();
      } else {
        this.afiliados = [];
      }
    },
    tipoDocSelected() {
      // Revalidar el formulario al cambiar el tipo de documento
      this.$nextTick(() => {
        if (this.$refs["paciente-form"]) {
          this.$refs["paciente-form"].validate();
          setTimeout(() => {
            this.$refs["paciente-form"]?.validate?.();
          }, 50);
        }
      });
    },
  },
  computed: {
    ...mapGetters({ isMobile: "menu/isMobile" }),
    pacienteApellidoNombre() {
      return `${this.pacienteApellido} ${this.pacienteNombre}`;
    },
    isDocumentoDNI() {
      // Cuando NO validaAfiliado, usamos tipos de documento propios
      // DNI => id = 1, value1 = 'DNI'
      return (
        this.tipoDocSelected &&
        (this.tipoDocSelected.id === 1 || this.tipoDocSelected.value1 === "DNI")
      );
    },
    shouldMaskDocumento() {
      return this.isDocumentoDNI;
    },
    showGenerateCuil() {
      const docOk =
        this.pacienteDocumento != null &&
        this.pacienteDocumento.toString().trim() !== "";
      const sexoOk = this.generoSelected != null;
      const cuilVacio =
        !this.pacienteCuil || this.pacienteCuil.toString().trim() === "";
      return this.isDocumentoDNI && docOk && sexoOk && cuilVacio;
    },
    canAddEstudio() {
      return (
        this.estudioActual.descripcion &&
        this.estudioActual.descripcion.trim() !== "" &&
        this.estudioActual.cantidad > 0 &&
        (this.editingIndex !== null || this.estudiosAgregados.length < 40)
      );
    },
    allEstudiosHaveContent() {
      return this.estudiosAgregados.length > 0;
    },
    isNextStepDisabled() {
      // Si está bloqueado, siempre deshabilitar
      if (this.prestadorValidation && this.prestadorValidation.isBlocked) {
        return true;
      }

      switch (this.step) {
        case 1:
          // Paso 1: Solo validar paciente y búsqueda de afiliado
          return !this.isPacienteFormValid || !this.afiliadoSearched;
        case 2:
          // Paso 2: Validar formulario de prescripción y que tenga al menos 1 estudio
          return !this.isPrescripcionFormValid || !this.allEstudiosHaveContent;
        case 3:
          // Paso 3: Siempre permitir finalizar si llegó hasta aquí
          return false;
        default:
          return true;
      }
    },
  },
  methods: {
    ...mapActions({
      getFinanciadoresByUser: "taxonomy/getFinanciadoresByUser",
      getPlanesByFinanciador: "taxonomy/getPlanesByFinanciador",
      getTiposDocumentos: "taxonomy/getTiposDocumentos",
      getTiposDocumentoByFinanciador: "taxonomy/getTiposDocumentoByFinanciador",
      getProvincias: "taxonomy/getProvincias",
      getPrestadorDataByUserIdAndFinanciadorId:
        "solicitud/getPrestadorDataByUserIdAndFinanciadorId",
      getValidationsFinanciador: "financiador/getValidations",
      setAlert: "user/setAlert",
      fetchAfiliadosFinanciadorByQuery:
        "taxonomy/fetchAfiliadosFinanciadorByQuery",
      getAfiliadoByNroAndFinanciador: "taxonomy/getAfiliadoByNroAndFinanciador",
      createPrescripcionEstudios: "solicitud/createPrescripcionEstudios",
    }),
    focusComponent(refName, timeout) {
      focusComponent(refName, timeout);
    },
    focusFirstFieldOfCurrentStep(event) {
      // Prevenir el comportamiento default del tab
      event.preventDefault();

      // Determinar el primer campo del step actual
      let firstFieldId = null;

      switch (this.step) {
        case 1:
          firstFieldId = "FinanciadorSelect";
          break;
        case 2:
          firstFieldId = "diagnosticoField";
          break;
        case 3:
          firstFieldId = "siguienteBtn"; // En step 3, el primer "campo" es el botón Finalizar
          break;
        default:
          firstFieldId = "FinanciadorSelect";
          break;
      }

      this.focusComponent(firstFieldId, 10);
    },
    onMatriculaChange(matricula) {
      if (matricula) {
        // Actualizar los datos del prestador con la matrícula seleccionada
        this.prestadorMatricula = matricula.matriculaProv.toString();
        this.matriculaSeleccionada = matricula.preXfinId;

        // Actualizar dirección de prescripción con la dirección de la matrícula seleccionada
        if (matricula.direccion && matricula.direccion.trim()) {
          this.domicilioPrescripcion = matricula.direccion;
        }

        // Actualizar los datos del prestador específicos de esta matrícula
        this.prestadorDataActual = {
          especialidad: matricula.preEspecialidad || null,
          entidadEmisora: matricula.entidadEmisoraMatricula || null,
          provincia: matricula.provinciaNombre || null,
          provinciaId: matricula.provinciaId || null,
          direccion: matricula.direccion || null,
          preXfinId: matricula.preXfinId,
        };
      }
    },
    addEstudio() {
      // Solo validar límite si estamos agregando un nuevo estudio (no editando)
      if (this.editingIndex === null && this.estudiosAgregados.length >= 40) {
        this.setAlert({
          type: "warning",
          message: "No es posible agregar más de 40 estudios complementarios.",
        });
        return;
      }

      // Verificar si ya existe un estudio con la misma descripción
      const existe = this.estudiosAgregados.some(
        (estudio, index) =>
          estudio.descripcion.toLowerCase().trim() ===
            this.estudioActual.descripcion.toLowerCase().trim() &&
          index !== this.editingIndex // Excluir el estudio que estamos editando
      );

      if (existe) {
        this.setAlert({
          type: "warning",
          message: "No es posible agregar el mismo estudio dos veces.",
        });
        return;
      }

      const nuevoEstudio = {
        id:
          this.editingIndex !== null
            ? this.estudiosAgregados[this.editingIndex].id
            : Date.now(),
        codigo: this.estudioActual.codigo || "",
        descripcion: this.estudioActual.descripcion.trim(),
        cantidad: Math.trunc(this.estudioActual.cantidad),
      };

      if (this.editingIndex !== null) {
        // Actualizar estudio existente
        this.estudiosAgregados.splice(this.editingIndex, 1, nuevoEstudio);
        this.editingIndex = null;
      } else {
        // Agregar nuevo estudio
        this.estudiosAgregados.push(nuevoEstudio);
      }

      // Limpiar formulario
      this.estudioActual = {
        codigo: null,
        descripcion: null,
        cantidad: 1,
      };

      // Focus en descripción para agregar otro
      this.focusComponent("descripcionEstudio", 100);
    },
    editEstudio(item) {
      this.editingIndex = this.estudiosAgregados.findIndex(
        (e) => e.id === item.id
      );
      this.estudioActual = {
        codigo: item.codigo,
        descripcion: item.descripcion,
        cantidad: item.cantidad,
      };
      this.focusComponent("descripcionEstudio", 10);
    },
    generarCUIL() {
      this.generatingCuil = true;
      try {
        const sexo = this.generoSelected?.id;
        const nroDoc = (this.pacienteDocumento || "")
          .toString()
          .replaceAll(".", "");
        if (!this.isDocumentoDNI || !sexo || !nroDoc) {
          this.generatingCuil = false;
          return;
        }

        // Simular operación asíncrona con timeout de 1 segundo
        setTimeout(() => {
          try {
            const cuil = cuilHelper.generarCuil(sexo, nroDoc);
            const formatted = `${cuil.substring(0, 2)}-${cuil.substring(2, 10)}-${cuil.substring(10)}`;
            this.pacienteCuil = formatted;
            this.$nextTick(() => this.$refs["paciente-form"]?.validate?.());
          } catch (error) {
            console.error("Error generando CUIL:", error);
          } finally {
            this.generatingCuil = false;
          }
        }, 1000);
      } catch (error) {
        console.error("Error generando CUIL:", error);
        this.generatingCuil = false;
      }
    },
    deleteEstudio(item) {
      this.estudiosAgregados = this.estudiosAgregados.filter(
        (e) => e.id !== item.id
      );
      // Si estábamos editando este elemento, cancelar la edición
      if (this.editingIndex !== null) {
        const editingItem = this.estudiosAgregados[this.editingIndex];
        if (editingItem && editingItem.id === item.id) {
          this.editingIndex = null;
          this.estudioActual = {
            codigo: null,
            descripcion: null,
            cantidad: 1,
          };
        }
      }
      this.focusComponent("descripcionEstudio", 10);
    },
    handlePacienteDataChange() {
      // Autogenerar CUIL si es DNI y tenemos todos los datos necesarios
      this.$nextTick(() => {
        this.generarCuilAutomatico();
      });
    },
    generarCuilAutomatico() {
      if (!this.isDocumentoDNI) return;

      const documento = this.pacienteDocumento?.toString().replace(/\D/g, '');
      const sexo = this.generoSelected?.id;

      if (!documento || !sexo) return;

      try {
        const cuilGenerado = cuilHelper.generarCuil(sexo, documento);
        const cuilFormateado = `${cuilGenerado.substring(0, 2)}-${cuilGenerado.substring(2, 10)}-${cuilGenerado.substring(10)}`;

        this.pacienteCuil = cuilFormateado;
      } catch (error) {
        console.error("Error generando CUIL automáticamente:", error);
      }
    },
    async setSelects() {
      const financiadores = await this.getFinanciadoresByUser();
      this.financiadoresItems = financiadores;
      if (this.financiadoresItems.length === 1) {
        this.financiadorSelected = financiadores[0];
        setTimeout(() => {
          const elementAfiliado = document.getElementById(
            "AfiliadoAutocomplete"
          );
          if (elementAfiliado) elementAfiliado.focus();
        }, 200);
        this.setDataFinanciador();
      } else {
        if (this.solicitudItem == null) {
          const selectComponent = document.getElementById("FinanciadorSelect");
          if (selectComponent) selectComponent.focus();
        }
      }
      this.generosItems = [
        { id: "M", value: "MASCULINO" },
        { id: "F", value: "FEMENINO" },
      ];
      const provincias = await this.getProvincias();
      this.provinciasItems = provincias;
    },
    setDefaultDates() {
      const currentDate = new Date();
      this.fechaEmision = currentDate;
      this.selectedFechaEmision = this.formatDate(currentDate);
      this.fechaNacimiento = currentDate;
      this.selectedFechaNacimiento = this.formatDate(currentDate);
      this.maxDateFechaNacimientoFormatted = helperDate.convertDateFormat(
        this.formatDate(currentDate)
      );
    },
    async setRecetaToCopy() {
      this.financiadorSelected = this.financiadoresItems.find(
        (x) =>
          x.id == this.solicitudItem.receta.paciente.cobertura.nroFinanciador
      );
      await this.setDataFinanciador();

      if (this.validaAfiliado)
        this.inputAfiliado = this.solicitudItem.receta.paciente.nroDoc;
      else {
        this.afiliadoSearched = true;
        this.planSelected = this.solicitudItem.receta.paciente.cobertura.plan;
        this.pacienteApellido = this.solicitudItem.receta.paciente.apellido;
        this.pacienteNombre = this.solicitudItem.receta.paciente.nombre;
        this.tipoDocSelected = this.tiposDocItems.find(
          (x) => x.value1 == this.solicitudItem.receta.paciente.tipoDoc
        );
        this.pacienteDocumento = this.solicitudItem.receta.paciente.nroDoc;
        this.pacienteCuil = this.solicitudItem.receta.paciente.cuil || "";
        this.generoSelected = this.generosItems.find(
          (x) => x.id == this.solicitudItem.receta.paciente.sexo
        );
        this.pacienteNumero =
          this.solicitudItem.receta.paciente.cobertura.numero;
        this.fechaNacimiento = new Date(
          this.solicitudItem.receta.paciente.fechaNacimiento
        );
        this.selectedFechaNacimiento = this.formatDate(
          new Date(this.solicitudItem.receta.paciente.fechaNacimiento)
        );
        this.pacienteEmail = this.solicitudItem.receta.paciente.email;
        this.pacienteTelefono = this.solicitudItem.receta.paciente.telefono;
      }

      this.diagnostico = this.solicitudItem.receta.diagnostico;
      // Intentar preservar la matrícula original si está disponible
      const numeroMatriculaOriginal =
        this.solicitudItem.receta?.medico?.matricula?.numero ||
        this.solicitudItem.nroMatricula;
      if (numeroMatriculaOriginal && this.matriculasDisponibles.length > 1) {
        const matriculaOriginal = this.matriculasDisponibles.find(
          (m) =>
            m.matriculaProv?.toString() === numeroMatriculaOriginal?.toString()
        );
        if (matriculaOriginal) {
          this.matriculaSeleccionada = matriculaOriginal.preXfinId;
          this.onMatriculaChange(matriculaOriginal);
        }
      }
      this.afiliadoWithUploadedData = true;

      // Manejar estudios prescritos
      if (
        this.solicitudItem.receta.prescripcion &&
        this.solicitudItem.receta.prescripcion.length > 0
      ) {
        this.estudiosAgregados = this.solicitudItem.receta.prescripcion.map(
          (item, index) => ({
            id: Date.now() + index,
            codigo: item.codPractica || "",
            descripcion: item.prescripcion,
            cantidad: item.cantidad || 1,
          })
        );
      } else {
        this.estudiosAgregados = [];
      }
    },
    async loadValidationsFinanciador() {
      if (this.financiadorSelected != null) {
        try {
          const validations = await this.getValidationsFinanciador(
            this.financiadorSelected?.id
          );
          this.validaAfiliado = validations.validaAfiliado;
          let futureDate = new Date();
          futureDate.setDate(
            futureDate.getDate() + (validations.diasEmisionFuturo - 1)
          );
          this.maxDateFechaEmisionFormatted = helperDate.convertDateFormat(
            helperDate.formatDate(futureDate)
          );
          this.maxDateFechaEmision = futureDate.setHours(0, 0, 0, 0);
          this.validaCobertura = validations.validaCobertura;
        } catch (error) {
          console.error(error);
        }
      }
    },
    async loadPrestadorData() {
      if (this.financiadorSelected != null) {
        try {
          this.prestadorValidation =
            await this.getPrestadorDataByUserIdAndFinanciadorId(
              this.financiadorSelected?.id
            );

          this.prestadorMatricula =
            this.prestadorValidation.prestadorData.matp.toString();
          this.prestadorApellidoNombre =
            this.prestadorValidation.prestadorData.nombre;
          this.prestadorData = this.prestadorValidation.prestadorData; // Usar el servicio unificado que ahora tiene todos los datos

          // Configurar matrículas disponibles
          this.matriculasDisponibles =
            this.prestadorValidation.matriculasDisponibles || [];

          // Buscar la matrícula por defecto (con esDefault = true) o la primera si no hay ninguna marcada
          const matriculaPorDefecto =
            this.matriculasDisponibles.find((m) => m.esDefault) ||
            this.matriculasDisponibles[0];

          // Configurar la matrícula seleccionada como la matrícula por defecto
          this.matriculaSeleccionada = matriculaPorDefecto?.preXfinId;

          // Configurar dirección de prescripción con la dirección de la matrícula por defecto si está disponible
          if (
            matriculaPorDefecto?.direccion &&
            matriculaPorDefecto.direccion.trim()
          ) {
            this.domicilioPrescripcion = matriculaPorDefecto.direccion;
          }

          // Configurar datos del prestador con la matrícula por defecto
          if (matriculaPorDefecto) {
            this.prestadorDataActual = {
              especialidad: matriculaPorDefecto.preEspecialidad || null,
              entidadEmisora:
                matriculaPorDefecto.entidadEmisoraMatricula || null,
              provincia: matriculaPorDefecto.provinciaNombre || null,
              provinciaId: matriculaPorDefecto.provinciaId || null,
              direccion: matriculaPorDefecto.direccion || null,
              preXfinId: matriculaPorDefecto.preXfinId,
            };
          }

          // Verificar si está bloqueado
          if (this.prestadorValidation.isBlocked) {
            // No mostrar setAlert porque ahora tenemos una alerta inline
            return; // No configurar datos si está bloqueado
          }

          // Mostrar warnings si los hay
          if (
            this.prestadorValidation.warnings &&
            this.prestadorValidation.warnings.length > 0
          ) {
            this.prestadorValidation.warnings.forEach((warning) => {
              this.setAlert({
                type: "warning",
                message: warning,
                timeout: 8000,
              });
            });
          }
        } catch (error) {
          console.error("Error al obtener datos del prestador:", error);
          this.setAlert({
            type: "error",
            message:
              "Error al validar los datos del prestador. Por favor contacte al administrador.",
            timeout: 5000,
          });
        }
      }
    },
    async setDataFinanciador() {
      this.afiliadoSearched = false;
      this.selectedAfiliado =
        this.searchAfiliadoField =
        this.inputAfiliado =
        this.planSelected =
        this.pacienteApellido =
        this.domicilioPrescripcion =
        this.pacienteNombre =
        this.tipoDocSelected =
        this.pacienteDocumento =
        this.generoSelected =
        this.pacienteNumero =
        this.fechaNacimiento =
        this.selectedFechaNacimiento =
        this.pacienteEmail =
        this.pacienteTelefono =
        this.diagnostico =
        this.prestadorData =
          null;
      // Resetear estudios prescritos
      this.estudiosAgregados = [];
      this.estudioActual = {
        codigo: null,
        descripcion: null,
        cantidad: 1,
      };
      this.editingIndex = null;
      this.pacienteCuil = "";
      this.setDefaultDates();
      try {
        // Paralelizar operaciones independientes para mejorar rendimiento
        await Promise.all([
          this.loadValidationsFinanciador(),
          this.loadTiposDoc() // ✅ No depende de datos del prestador
        ]);

        // Cargar datos del prestador en background (sin bloquear UI crítica)
        const prestadorPromise = this.loadPrestadorData();

        // Cargar planes si es necesario (depende de validaAfiliado)
        let planesPromise = Promise.resolve();
        if (this.validaAfiliado) {
          planesPromise = this.loadPlanes();
        }

        // Esperar a que las operaciones independientes terminen
        await planesPromise;

        // Verificar si el prestador está bloqueado
        await prestadorPromise;
        if (this.prestadorValidation && this.prestadorValidation.isBlocked) {
          // Resetear validación para que no interfiera
          this.isPacienteFormValid = false;
          return;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async loadPlanes() {
      if (this.financiadorSelected != null) {
        const planes = await this.getPlanesByFinanciador({
          financiadorId: this.financiadorSelected?.id,
          fecha: this.selectedFechaEmision,
        });
        this.planesItems = planes;
      } else {
        this.planesItems = [];
        this.planSelected = null;
      }
    },
    async loadTiposDoc() {
      const tiposDoc = await this.getTiposDocumentos();
      this.tiposDocItems = tiposDoc;
    },
    selectFechaEmision(event) {
      this.selectedFechaEmision = event.dateString;
      this.fechaEmision = event.date;
      if (this.financiadorSelected != null && this.selectedAfiliado != null) {
        this.afiliadoWithUploadedData = true;
        this.setAfiliado(this.selectedAfiliado);
      }
    },
    selectFechaNacimiento(event) {
      this.selectedFechaNacimiento = event.dateString;
      this.fechaNacimiento = event.date;
    },
    async findAfiliado() {
      if (this.timerAfiliado) {
        clearTimeout(this.timerAfiliado);
        this.timerAfiliado = null;
      }
      this.timerAfiliado = setTimeout(async () => {
        if (this.afiliados?.some((x) => x.title == this.inputAfiliado)) {
          return;
        } else if (!this.afiliadoWithUploadedData) {
          this.loadingAfiliados = true;
          const response = await this.fetchAfiliadosFinanciadorByQuery({
            fecha: this.selectedFechaEmision,
            input: this.inputAfiliado.toString().trim(),
            financiadorId: this.financiadorSelected?.id,
          });
          this.$refs["afiliados-autocomplete"].cachedItems = [];
          this.afiliados = response;
          this.loadingAfiliados = false;
          if (this.selectedAfiliado) {
            this.selectedAfiliado = response[0];
            this.setsAlertsAfiliado(this.selectedAfiliado);
          }
        } else {
          this.loadingAfiliados = true;
          const response = await this.fetchAfiliadosFinanciadorByQuery({
            fecha: this.selectedFechaEmision,
            input: this.inputAfiliado.toString().trim(),
            financiadorId: this.financiadorSelected?.id,
          });
          this.$refs["afiliados-autocomplete"].cachedItems = [];
          this.afiliados = response;
          this.setAfiliado(response[0]);
          this.loadingAfiliados = this.afiliadoWithUploadedData = false;
        }
      }, 1000);
    },
    setsAlertsAfiliado(selectedAfiliado) {
      switch (selectedAfiliado.estadoBenef.id) {
        case 1:
          this.setAlert({
            type: "success",
            message: "El afiliado posee cobertura.",
          });
          break;
        case 2:
          this.setAlert({
            type: "warning",
            message: "El afiliado se encuentra sin cobertura por baja.",
          });
          break;
        case 3:
          this.setAlert({
            type: "warning",
            message: "El afiliado se encuentra momentaneamente sin cobertura.",
          });
          break;
        case 4:
          this.setAlert({
            type: "warning",
            message:
              "El afiliado se encuentra momentaneamente sin cobertura, en gestión judicial.",
          });
          break;
        default:
          this.setAlert({
            type: "warning",
            message: "El afiliado se encuentra sin cobertura.",
          });
          break;
      }
    },
    setAfiliado(afiliado) {
      this.afiliadoSearched = true;
      if (this.afiliadoWithUploadedData) this.selectedAfiliado = afiliado;
      else this.selectedAfiliado = afiliado.raw;
      this.setsAlertsAfiliado(this.selectedAfiliado);
      this.planSelected = this.planesItems.find(
        (x) => x.id == this.selectedAfiliado.planId
      );
      this.pacienteApellido = this.selectedAfiliado.benApe;
      this.pacienteNombre = this.selectedAfiliado.benNom;
      this.tipoDocSelected = this.tiposDocItems.find(
        (x) => x.id == this.selectedAfiliado.tipoDocId
      );
      this.pacienteDocumento = this.selectedAfiliado.docId;
      this.pacienteCuil = this.selectedAfiliado.cuil || "";
      this.generoSelected = this.generosItems.find(
        (x) => x.id == this.selectedAfiliado.genero
      );
      this.pacienteNumero = this.selectedAfiliado.nroAfiliado;
      this.pacienteEmail = this.selectedAfiliado.email;
      this.pacienteTelefono = this.selectedAfiliado.telefono;
      this.selectedFechaNacimiento = this.selectedAfiliado.fechaNacimiento;
      const partesFecha = this.selectedFechaNacimiento.split("/");
      const fecha = new Date(
        partesFecha[2],
        partesFecha[1] - 1,
        partesFecha[0]
      );
      this.fechaNacimiento = fecha;
      this.inputAfiliado = this.selectedAfiliado.docId.toString();
      this.$refs["paciente-form"].validate();
      this.seleccionarSiguiente();
    },
    seleccionarSiguiente() {
      setTimeout(() => {
        let blurHelper = document.getElementById("blurHelper");
        blurHelper?.focus?.();
        let componente = document.getElementById("siguienteBtn");
        componente?.focus?.();
      }, 200);
    },
    customFilterAfiliado(item) {
      return item.nroAfiliado || item.benNombreCompleto || item.docId;
    },
    formatDate(date) {
      return helperDate.formatDate(date);
    },
    nextStep() {
      if (this.step < this.steps.length) {
        this.step++;
        switch (this.step) {
          case 2:
            // Reforzar fecha nacimiento si se reseteó
            this.selectedFechaNacimiento = this.formatDate(
              this.fechaNacimiento
            );
            // Establecer validación de prescripción a false para forzar validación
            this.isPrescripcionFormValid = false;
            // Validar el formulario de prescripción en el siguiente tick
            this.$nextTick(() => {
              if (
                this.$refs["prescripcion-form"] &&
                this.diagnostico != null &&
                this.domicilioPrescripcion != null
              ) {
                this.$refs["prescripcion-form"].validate();
              }
              // Focus en el primer campo del step 2 (Diagnóstico)
              this.focusComponent("diagnosticoField", 200);
            });
            break;
          case 3:
            // Focus en el botón "Finalizar" del step 3
            this.$nextTick(() => {
              this.focusComponent("siguienteBtn", 200);
            });
            break;
          default:
            break;
        }
        if (this.step === this.steps.length) {
          this.nextText = "Finalizar";
        }
      } else {
        this.createRecipe();
      }
    },
    prevStep() {
      if (this.step == 3) this.nextText = "Siguiente";
      if (this.step > 0) {
        this.step--;
        // Focus automático al volver a un step anterior
        this.$nextTick(() => {
          switch (this.step) {
            case 1:
              // Focus en el primer campo del step 1 (Financiador)
              this.focusComponent("FinanciadorSelect", 200);
              break;
            case 2:
              // Focus en el primer campo del step 2 (Diagnóstico)
              this.focusComponent("diagnosticoField", 200);
              break;
            default:
              break;
          }
        });
      }
    },
    closeModal(reload) {
      this.$emit("closeModal", reload);
    },
    async searchAfiliado() {
      this.isLoadingSearchAfiliado = true;
      const data = {
        numero: this.searchAfiliadoField,
        financiadorId: this.financiadorSelected?.id,
      };
      try {
        const response = await this.getAfiliadoByNroAndFinanciador(data);
        if (response != null) {
          this.pacienteApellido = response.benApe;
          this.pacienteNombre = response.benNom;
          this.pacienteDocumento = response.docId;
          this.pacienteCuil = response.cuil || "";
          this.pacienteEmail = response.email;
          this.fechaNacimiento = new Date(
            Date.parse(response.fechaNacimiento + "T00:00:00")
          );
          this.selectedFechaNacimiento = this.formatDate(this.fechaNacimiento);
          this.generoSelected = this.generosItems.find(
            (x) => x.id == response.genero
          );
          this.pacienteNumero = response.nroAfiliado;
          this.planSelected = response.plan;
          this.pacienteTelefono = response.telefono;
          this.tipoDocSelected = this.tiposDocItems.find(
            (x) => x.value1 == response.tipoDoc
          );
          this.setAlert({
            type: "success",
            message:
              "El afiliado ha sido encontrado en el sistema. A continuación se cargarán sus respectivos datos, por favor verificar los mismos.",
          });
        } else {
          this.setAlert({
            type: "info",
            message:
              "El afiliado no ha sido encontrado en el sistema. Cargue sus datos para continuar con la prescripción.",
          });
        }
        this.afiliadoSearched = true;
        this.$nextTick(() => {
          this.$refs["paciente-form"].validate();
          setTimeout(() => {
            this.$refs["paciente-form"].validate();
          }, 100);
        });
      } catch (error) {
        console.error(error);
      }
      this.isLoadingSearchAfiliado = false;
    },
    async createRecipe() {
      this.loadingCreateRecipeBtn = true;
      try {
        // Obtener datos de la matrícula seleccionada
        const matriculaSeleccionadaData = this.matriculasDisponibles.find(
          m => m.preXfinId === this.matriculaSeleccionada
        ) || this.matriculasDisponibles[0];
        
        const request = {
          paciente: {
            apellido: this.pacienteApellido,
            nombre: this.pacienteNombre,
            tipoDoc: this.tipoDocSelected.value1,
            nroDoc:
              this.isDocumentoDNI
                ? (this.pacienteDocumento || "")
                    .toString()
                    .replaceAll(".", "")
                : this.pacienteDocumento.toString(),
            cuil:
              this.pacienteCuil != null &&
              this.pacienteCuil.toString().trim() !== ""
                ? this.pacienteCuil.toString().replaceAll("-", "")
                : this.pacienteCuil,
            sexo: this.generoSelected.id,
            fechaNacimiento: this.fechaNacimiento,
            email: this.pacienteEmail,
            telefono: this.pacienteTelefono,
            cobertura: {
              nroFinanciador: this.financiadorSelected.id.toString(),
              plan: this.validaAfiliado
                ? this.planSelected.value
                : this.planSelected,
              numero: this.pacienteNumero,
            },
          },
          medico: {
            apellido: this.prestadorData?.apellido || "",
            nombre: this.prestadorData?.nombreSolo || "",
            tipoDoc: this.prestadorData?.tipoDoc || "",
            nroDoc:
              this.prestadorData?.docId?.toString()?.replaceAll(".", "") || "",
            fechaNacimiento: this.prestadorData?.fechaNacimiento || null,
            sexo: this.prestadorData?.sexo || null,
            matricula: {
              tipo: "mp",
              numero: matriculaSeleccionadaData?.matriculaProv?.toString() || this.prestadorMatricula,
              provincia: matriculaSeleccionadaData?.provinciaNombre || "",
            },
            especialidad: matriculaSeleccionadaData?.preEspecialidad || "",
            preXfinId: this.matriculaSeleccionada,
          },
          // Estructura según el backend: List<PrescripcionItemDTO>
          prescripcion: this.estudiosAgregados.map((estudio) => ({
            codPractica: estudio.codigo || null,
            prescripcion: estudio.descripcion,
            cantidad: estudio.cantidad.toString(),
            codigoDiagnostico: null,
            diagnostico: null,
            observaciones: null,
          })),
          diagnostico: this.diagnostico,
          direccionConsultorio: this.domicilioPrescripcion,
          fechaEmision: this.fechaEmision,
          observaciones: "",
          indicaciones: "",
          imprimirDiagnostico: "S",
        };
        const response = await this.createPrescripcionEstudios(request);
        if (response.status == 200) {
          this.setAlert({
            type: "success",
            message:
              "Prescripción de estudios complementarios creada exitosamente.",
          });
          this.recipeLink = response.data.link;
          this.solicitudId = response.data.numero;
          this.dialogSendRecipe = true;
        }
      } catch (error) {
        console.error("Error creating prescription:", error);
        this.setAlert({
          type: "error",
          message:
            "Error al crear la prescripción de estudios. Verifique los datos ingresados.",
        });
      } finally {
        this.loadingCreateRecipeBtn = false;
      }
    },
  },
};
</script>

<style scoped>
.center-input >>> input {
  text-align: center;
}

/* Mejorar espaciado de mensajes de error */
.v-textarea .v-messages {
  margin-top: 4px;
  min-height: 20px;
}

/* Asegurar centrado vertical del botón eliminar */
.d-flex.align-center {
  min-height: 56px;
}
</style>
